/** @format */

import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { CartItem, ShoppingCart } from "../types";
import toast from "react-hot-toast";

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, "id" | "addedAt">) => void;
  removeFromCart: (itemId: string) => void;
  updateCartItemQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartSubtotal: () => number;
  getCartItemCount: () => number;
  loading: boolean;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        // Convert string dates back to Date objects and ensure price is a number
        const itemsWithDates = parsedCart.map((item: any) => ({
          ...item,
          price: Number(item.price), // Ensure price is a number
          addedAt: new Date(item.addedAt),
        }));
        setCartItems(itemsWithDates);
      } catch (error) {
        console.error("Error parsing cart from localStorage:", error);
        localStorage.removeItem("cart");
      }
    }
    setLoading(false);
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cartItems.length > 0) {
      localStorage.setItem("cart", JSON.stringify(cartItems));
    } else {
      localStorage.removeItem("cart");
    }
  }, [cartItems]);

  // Debounced sync to Firestore for authenticated users
  const syncTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!currentUser) return;
    // Debounce to batch rapid changes and reduce writes
    if (syncTimerRef.current) {
      window.clearTimeout(syncTimerRef.current);
    }
    syncTimerRef.current = window.setTimeout(() => {
      syncCartWithFirestore();
    }, 1000);
    return () => {
      if (syncTimerRef.current) {
        window.clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
    };
  }, [currentUser, cartItems]);

  const syncCartWithFirestore = async () => {
    if (!currentUser) return;

    try {
      const cartRef = doc(db, "carts", currentUser.uid);
      // Use upsert with merge to avoid a read-before-write
      const cartData: ShoppingCart = {
        userId: currentUser.uid,
        items: cartItems,
        total: getCartTotal(),
        updatedAt: new Date(),
      };
      await setDoc(cartRef, cartData, { merge: true });
    } catch (error) {
      console.error("Error syncing cart with Firestore:", error);
    }
  };

  const loadCartFromFirestore = async () => {
    if (!currentUser) return;

    try {
      const cartRef = doc(db, "carts", currentUser.uid);
      const cartDoc = await getDoc(cartRef);

      if (cartDoc.exists()) {
        const cartData = cartDoc.data() as ShoppingCart;
        // Convert Firestore timestamps to Date objects and ensure price is a number
        const itemsWithDates = cartData.items.map((item: any) => ({
          ...item,
          price: Number(item.price), // Ensure price is a number
          addedAt: item.addedAt?.toDate
            ? item.addedAt.toDate()
            : new Date(item.addedAt),
        }));
        setCartItems(itemsWithDates);
      }
    } catch (error) {
      console.error("Error loading cart from Firestore:", error);
    }
  };

  const addToCart = (item: Omit<CartItem, "id" | "addedAt">) => {
    const newItem: CartItem = {
      ...item,
      id: `${item.type}-${item.itemId}-${Date.now()}`,
      addedAt: new Date(),
    };

    // Check if item already exists
    const existingItemIndex = cartItems.findIndex(
      (cartItem) =>
        cartItem.itemId === item.itemId && cartItem.type === item.type
    );

    if (existingItemIndex !== -1) {
      toast.error("Item already in cart!");
      return;
    }

    setCartItems((prev) => [...prev, newItem]);
    toast.success("Added to cart!");
  };

  const removeFromCart = (itemId: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
    toast.success("Removed from cart!");
  };

  const updateCartItemQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    setCartItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => {
    setCartItems([]);
    toast.success("Cart cleared!");
  };

  const getCartSubtotal = () => {
    return cartItems.reduce((total, item) => total + Number(item.price || 0), 0);
  };

  const getCartTotal = () => {
    return getCartSubtotal();
  };

  const getCartItemCount = () => {
    return cartItems.length;
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen(!isCartOpen);

  const value: CartContextType = {
    cartItems,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    getCartTotal,
    getCartSubtotal,
    getCartItemCount,
    loading,
    isCartOpen,
    openCart,
    closeCart,
    toggleCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
